import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from '@/components/toast';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { useOrgId } from '@/lib/org';
import { errorText, type Approval } from '@/lib/portal';
import { DecisionDialog } from './decision-dialog';

const docName = (a: Approval) =>
  `${a.document.kind === 'invoice' ? 'invoice' : 'bill'}${a.document.number ? ` ${a.document.number}` : ''} for ${a.document.contactName}`;

/** Approve, Reject and Withdraw for one request (queue rows and the document strip). */
export function ApprovalActions({
  a,
  size = 'sm',
  onDone,
}: {
  a: Approval;
  size?: 'sm' | 'default';
  onDone?: () => void;
}) {
  const orgId = useOrgId();
  const qc = useQueryClient();
  const [dialog, setDialog] = useState<'approve' | 'reject' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const act = useMutation({
    mutationFn: ({
      action,
      comment,
    }: {
      action: 'approve' | 'reject' | 'withdraw';
      comment?: string;
    }) =>
      api<Approval>(`/orgs/${orgId}/approvals/${a.id}/${action}`, {
        method: 'POST',
        body: JSON.stringify(comment ? { comment } : {}),
      }),
    onSuccess: async (r, v) => {
      setDialog(null);
      setError(null);
      toast(
        v.action === 'withdraw'
          ? 'Approval request withdrawn.'
          : r.status === 'approved'
            ? r.direction === 'to_staff'
              ? `Approved: the ${docName(r)} is posted.`
              : `Approved: your accountant will post the ${docName(r)}.`
            : `Rejected: the ${docName(r)} goes back with your comment.`,
      );
      await qc.invalidateQueries();
      onDone?.();
    },
    onError: (e) => {
      if (dialog) setError(errorText(e));
      else toast(errorText(e), { tone: 'error' });
    },
  });
  if (!a.canDecide && !a.canWithdraw) return null;
  return (
    <span className="inline-flex items-center gap-2">
      {a.canDecide ? (
        <>
          <Button size={size} onClick={() => setDialog('approve')}>
            {a.direction === 'to_staff' ? 'Approve and post' : 'Approve'}
          </Button>
          <Button size={size} variant="outline" onClick={() => setDialog('reject')}>
            Reject
          </Button>
        </>
      ) : null}
      {a.canWithdraw ? (
        <Button
          size={size}
          variant="ghost"
          disabled={act.isPending}
          onClick={() => {
            if (window.confirm('Withdraw this approval request?'))
              act.mutate({ action: 'withdraw' });
          }}
        >
          Withdraw
        </Button>
      ) : null}
      <DecisionDialog
        open={dialog === 'approve'}
        title={`Approve the ${docName(a)}?`}
        description={
          a.direction === 'to_staff'
            ? 'Approving posts it to the books with the next number. Your comment goes to the client.'
            : 'Your accounting team posts it after your approval. Your comment goes to your accountant.'
        }
        confirm={a.direction === 'to_staff' ? 'Approve and post' : 'Approve'}
        label="Comment"
        pending={act.isPending}
        error={error}
        onConfirm={(comment) => act.mutate({ action: 'approve', comment })}
        onClose={() => {
          setDialog(null);
          setError(null);
        }}
      />
      <DecisionDialog
        open={dialog === 'reject'}
        title={`Reject the ${docName(a)}?`}
        description="It stays a draft. Say what needs to change; the comment goes with the rejection."
        confirm="Reject"
        required
        label="Comment"
        pending={act.isPending}
        error={error}
        onConfirm={(comment) => act.mutate({ action: 'reject', comment })}
        onClose={() => {
          setDialog(null);
          setError(null);
        }}
      />
    </span>
  );
}
